import React, { useState, useEffect } from "react";
import { Trash2, Plus, X, Save, Building2 } from "lucide-react";
import Loader from "../../components/common/Loader";
import axios from "axios";
import Swal from "sweetalert2";

const BankAccount = () => {
  const [bankAccount, setBankAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    accountHolder: "",
    accountNumber: "",
    ifsc: "",
    bankName: "",
    branch: "",
  });

  // ✅ Fetch bank account
  const fetchBankAccount = async () => {
    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors
      await new Promise((resolve) => setTimeout(resolve, 800));

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/bank/get`
      );

      if (response?.data?.data) {
        setBankAccount(response.data.data);
      } else {
        setBankAccount(null);
      }
    } catch (err) {
      console.error("Error fetching bank account:", err);
      // Check if it's a "No account found" error (not a real error)
      if (
        err.response?.data?.message === "No account found" ||
        err.response?.status === 404
      ) {
        setBankAccount(null);
        setError(null); // This is expected, not an error
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBankAccount();
  }, []);

  const handleDelete = async (accountId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#1b0a0a",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/bank/delete/${accountId}`
      );

      // Immediately clear the bank account state
      setBankAccount(null);
      setError(null);

      Swal.fire({
        title: "Deleted!",
        text: "Bank account has been deleted successfully.",
        icon: "success",
        confirmButtonColor: "#1b0a0a",
      });

      // Fetch updated data from backend
      fetchBankAccount();
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while deleting the bank account.",
        icon: "error",
        confirmButtonColor: "#1b0a0a",
      });
    }
  };

  // ✅ Add handler
  const handleAddAccount = () => {
    setFormData({
      accountHolder: "",
      accountNumber: "",
      ifsc: "",
      bankName: "",
      branch: "",
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({
      accountHolder: "",
      accountNumber: "",
      ifsc: "",
      bankName: "",
      branch: "",
    });
    setIsSubmitting(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.accountHolder ||
      !formData.accountNumber ||
      !formData.ifsc ||
      !formData.bankName ||
      !formData.branch
    ) {
      Swal.fire({
        title: "Validation Error",
        text: "All fields are required!",
        icon: "warning",
        confirmButtonColor: "#1b0a0a",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/bank/create`,
        formData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      Swal.fire({
        title: "Success!",
        text: "Bank account created successfully!",
        icon: "success",
        confirmButtonColor: "#1b0a0a",
      });

      handleCloseModal();

      // ✅ Small delay ensures backend writes are done
      setTimeout(fetchBankAccount, 500);
    } catch (error) {
      console.error("Error saving bank account:", error);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while creating the bank account.",
        icon: "error",
        confirmButtonColor: "#1b0a0a",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 mt-6 flex justify-between items-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor">
            Bank Account
          </h1>
          {!bankAccount && (
            <button
              className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
              onClick={handleAddAccount}
            >
              <Plus size={20} />
              Add Bank Account
            </button>
          )}
        </div>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              Error Loading Account
            </h3>
            <p className="text-secondaryTextColor">{error}</p>
          </div>
        ) : bankAccount ? (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-secondaryColor to-blue-700 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <Building2 size={32} />
                <h2 className="text-2xl font-bold">{bankAccount.bankName}</h2>
              </div>
              <p className="text-blue-100">{bankAccount.branch}</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor uppercase tracking-wide">
                    Account Holder
                  </label>
                  <p className="text-lg text-primaryTextColor font-medium mt-1">
                    {bankAccount.accountHolder}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor uppercase tracking-wide">
                    Account Number
                  </label>
                  <p className="text-lg text-primaryTextColor font-mono font-medium mt-1">
                    {bankAccount.accountNumber}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor uppercase tracking-wide">
                    IFSC Code
                  </label>
                  <p className="text-lg text-primaryTextColor font-mono font-medium mt-1">
                    {bankAccount.ifsc}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-secondaryTextColor uppercase tracking-wide">
                    Branch
                  </label>
                  <p className="text-lg text-primaryTextColor font-medium mt-1">
                    {bankAccount.branch}
                  </p>
                </div>
              </div>

              <div className="flex justify-end mt-6 pt-6 border-t">
                <button
                  onClick={() => handleDelete(bankAccount._id)}
                  className="bg-primaryColor text-white px-5 py-2.5 rounded-lg hover:bg-opacity-90 transition-all flex items-center gap-2 shadow-md"
                >
                  <Trash2 size={18} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-lg shadow-md">
            <div className="text-6xl mb-4">🏦</div>
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              No Bank Account Found
            </h3>
            <p className="text-secondaryTextColor mb-6">
              Add your organization's bank account to get started
            </p>
            <button
              onClick={handleAddAccount}
              className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300 shadow-md"
            >
              <Plus size={20} />
              Add Bank Account
            </button>
          </div>
        )}
      </div>

      {/* ✅ Add Account Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b text-primaryTextColor">
              <h2 className="text-2xl font-bold">Add Bank Account</h2>
              <button onClick={handleCloseModal} disabled={isSubmitting}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                {[
                  "accountHolder",
                  "accountNumber",
                  "ifsc",
                  "bankName",
                  "branch",
                ].map((field) => (
                  <div key={field}>
                    <label className="block text-sm font-semibold text-primaryTextColor mb-2 capitalize">
                      {field.replace(/([A-Z])/g, " $1")} *
                    </label>
                    <input
                      type="text"
                      name={field}
                      value={formData[field]}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondaryColor focus:border-transparent text-primaryTextColor"
                      placeholder={`Enter ${field.replace(/([A-Z])/g, " $1")}`}
                      disabled={isSubmitting}
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2.5 border border-gray-300 text-primaryTextColor rounded-lg hover:bg-gray-100 transition-all"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 transition-all disabled:opacity-50 shadow-md"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save size={18} /> Create Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccount;
